import {Ethnicity} from "./Ethnicity";

interface DemographicDataEntry {
    state: string;
    [year: number]: number;
    ethnicity: Ethnicity;
}


class DemographicData {
    get data(): DemographicDataEntry[] {
        return this._data;
    }
    private _data: DemographicDataEntry[] = [];

    constructor(preloadedData: any[]) {
        this.loadData(preloadedData);
    }

    static entryFromRow(row: { [key: string]: string }): DemographicDataEntry {
        const rowKey = Object.keys(row)[0];
        const columns = row[rowKey].split(';');
        return {
            state: columns[1].replace(/\s/g, ''),
            1976: +columns[2],
            1980: +columns[3],
            1984: +columns[4],
            1988: +columns[5],
            1992: +columns[6],
            1996: +columns[7],
            2000: +columns[8],
            2004: +columns[9],
            2008: +columns[10],
            2012: +columns[11],
            2016: +columns[12],
            2020: +columns[13],
            ethnicity: columns[14] as Ethnicity
        };
    }

    private loadData(data: any[]): void {
        data.forEach((d: any) => {
            this._data.push(DemographicData.entryFromRow(d));
        });
    }

    public getStates(): string[] {
        return Array.from(new Set(this._data.map(entry => entry.state)));
    }

    public getValueForStateAndYear(state: string, year: number): number | undefined {
        const entry = this._data.find(entry => entry.state === state);
        return entry ? entry[year] : undefined;
    }

    public getStateData(state: string): DemographicDataEntry[] {
        return this._data.filter(entry => entry.state === state);
    }

    public getValueForStateYearAndKey(state: string, year: number, key: number): number | undefined {
        const entry = this._data.find(entry => entry.state === state && entry[key] !== undefined);
        return entry ? entry[key] : undefined;
    }

    public getValuesForYearAndEthnicity(year: number, ethnicity: Ethnicity): Map<string, number> {
        const result = new Map<string, number>();

        this._data.forEach(entry => {
            if (entry[year] !== undefined && entry.ethnicity === ethnicity) {
                result.set(entry.state, entry[year]);
            }
        });

        return result;
    }

    /**
     * Returns the value for a given year, ethnicity and state
     * @param year
     * @param ethnicity
     * @param state
     */
    public getValueForYearAndEthnicityAndState(year: number, ethnicity: Ethnicity, state: string): number | undefined {
        const values = this.getValuesForYearAndEthnicity(year, ethnicity);
        return values.get(state);
    }

    /**
     * Returns the value for a given year and state, but for a set of Ethnicities.
     * @param year
     * @param ethnicities
     * @param state
     */
    public getValuesForYearAndEthnicitiesAndState(year: number, ethnicities: Set<Ethnicity>, state: string): Map<string, number> {
        const values = new Map<string, number>();

        if(ethnicities.size === 0) {
            this.getStates().forEach(state => values.set(state, 0));
            return values;
        }

        ethnicities.forEach(ethnicity => {
            const value = this.getValueForYearAndEthnicityAndState(year, ethnicity, state);
            values.set(ethnicity, value || 0);
        })

        return values
    }
}

export default DemographicData;